<template>
  <div class="fb-setup">
    <div class="fb-main">
      <p class="fb-lead">
        On the FluidBox, open Settings → Wi-Fi setup. Wait until a PIN is on the
        screen, then continue here.
      </p>

      <p v-if="!bleOk" class="fb-android-only">{{ androidOnlyMessage }}</p>

      <template v-else>
        <p v-if="deviceName" class="fb-device">Connected to {{ deviceName }}</p>
        <p
          v-if="statusText"
          class="fb-status"
          :class="{ error: statusIsError }"
        >
          {{ statusText }}
        </p>
        <p v-if="errorMessage" class="fb-status error">{{ errorMessage }}</p>

        <button
          v-if="!connected"
          type="button"
          class="fb-button"
          :disabled="busy"
          @click="findFluidBox"
        >
          {{ busy ? 'Looking…' : 'Find FluidBox' }}
        </button>

        <template v-if="connected && !success">
          <div class="fb-scan-list" v-if="scanResults.length">
            <button
              v-for="ap in scanResults"
              :key="ap.s"
              type="button"
              class="fb-ap"
              :class="{ selected: selectedSsid === ap.s }"
              @click="selectSsid(ap.s)"
            >
              <span class="fb-ap-name">{{ ap.s }}</span>
              <span class="fb-ap-rssi">{{ ap.r }} dBm</span>
            </button>
          </div>
          <p v-else class="fb-hint">
            Waiting for networks from the FluidBox, or type a hidden SSID below.
          </p>

          <div class="fb-field">
            <label for="fb-ssid">Network name</label>
            <input
              id="fb-ssid"
              v-model="manualSsid"
              type="text"
              maxlength="32"
              autocomplete="off"
              placeholder="SSID"
            />
          </div>
          <div class="fb-field">
            <label for="fb-password">Password</label>
            <div class="fb-password-wrap">
              <input
                id="fb-password"
                v-model="wifiPassword"
                :type="showWifiPassword ? 'text' : 'password'"
                maxlength="64"
                autocomplete="new-password"
                placeholder="Wi-Fi password"
              />
              <button
                type="button"
                class="fb-password-toggle"
                :aria-label="
                  showWifiPassword ? 'Hide password' : 'Show password'
                "
                :aria-pressed="showWifiPassword"
                @click="showWifiPassword = !showWifiPassword"
              >
                <i
                  class="fas"
                  :class="showWifiPassword ? 'fa-eye-slash' : 'fa-eye'"
                  aria-hidden="true"
                ></i>
              </button>
            </div>
          </div>
          <div class="fb-field">
            <label for="fb-pin">PIN on the FluidBox</label>
            <input
              id="fb-pin"
              v-model="pin"
              type="text"
              inputmode="numeric"
              maxlength="4"
              autocomplete="one-time-code"
              placeholder="4 digits"
            />
          </div>

          <button
            type="button"
            class="fb-button"
            :disabled="!canCommit"
            @click="saveAndConnect"
          >
            {{ committing ? 'Connecting…' : 'Save & connect' }}
          </button>
          <button
            type="button"
            class="fb-button fb-button-secondary"
            :disabled="busy || committing"
            @click="forgetNetwork"
          >
            Forget network
          </button>
        </template>

        <button v-if="success" type="button" class="fb-button" @click="finish">
          Done
        </button>
      </template>

      <button
        v-if="!success"
        type="button"
        class="fb-button fb-button-cancel"
        @click="cancel"
      >
        Back
      </button>
    </div>

    <div class="fb-diag">
      <div class="fb-diag-head">
        <span>FluidBox debug</span>
        <button type="button" class="fb-diag-clear" @click="clearDiag">
          clear
        </button>
      </div>
      <div ref="diagEl" class="fb-diag-body">{{ diagText }}</div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import fluidBoxWifi from '@/utils/fluidBoxWifi';

const {
  ANDROID_ONLY_MESSAGE,
  CHAR_CONTROL,
  CHAR_PASSWORD,
  CHAR_SCAN,
  CHAR_SSID,
  CHAR_STATUS,
  COMMIT_TIMEOUT_MS,
  CONNECT_TIMEOUT_MS,
  CONTROL,
  SERVICE_UUID,
  bleDropMeansWifiJoined,
  canUseWebBluetooth,
  chooserErrorMessage,
  decodeUtf8,
  digitsOnly,
  encodePasswordPayload,
  encodeUtf8,
  fluidBoxRequestDeviceOptions,
  parseScanJson,
  parseStatusJson,
  statusLabel,
} = fluidBoxWifi;

const emit = defineEmits(['close']);

const androidOnlyMessage = ANDROID_ONLY_MESSAGE;
const bleOk = canUseWebBluetooth();
const busy = ref(false);
const connected = ref(false);
const committing = ref(false);
const success = ref(false);
const deviceName = ref('');
const scanResults = ref([]);
const selectedSsid = ref('');
const manualSsid = ref('');
const wifiPassword = ref('');
const showWifiPassword = ref(false);
const pin = ref('');
const status = ref(null);
const errorMessage = ref('');
const diagLines = ref([]);
const diagEl = ref(null);
const diagText = computed(() => diagLines.value.join('\n'));

function errDetail(err) {
  if (!err) return 'unknown error';
  const name = err.name ? `${err.name}: ` : '';
  return `${name}${err.message || String(err)}`;
}

function diag(message) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  diagLines.value.push(`[${stamp}] ${message}`);
  nextTick(() => {
    const el = diagEl.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
}

function clearDiag() {
  diagLines.value = [];
}

const statusText = computed(() => statusLabel(status.value));
const statusIsError = computed(() => {
  const state = status.value && status.value.state;
  return state === 'bad_pin' || state === 'fail';
});
const canCommit = computed(() => {
  const ssid = (manualSsid.value || '').trim();
  return (
    connected.value &&
    !busy.value &&
    !committing.value &&
    ssid.length > 0 &&
    digitsOnly(pin.value).length === 4
  );
});

let bleDevice = null;
let gattServer = null;
let charSsid = null;
let charPassword = null;
let charControl = null;
let charStatus = null;
let charScan = null;
let closed = false;
let commitWait = null;
let commitSent = false;

function clearCommitWait(result) {
  if (!commitWait) return;
  clearTimeout(commitWait.timer);
  const pending = commitWait;
  commitWait = null;
  if (result !== undefined) pending.resolve(result);
}

function onDisconnected() {
  if (closed) return;
  if (
    bleDropMeansWifiJoined({
      commitSent,
      success: success.value,
      state: status.value && status.value.state,
    })
  ) {
    const typedSsid = (manualSsid.value || '').trim();
    status.value = {
      state: 'got_ip',
      ssid: (status.value && status.value.ssid) || typedSsid,
      rssi: status.value ? Number(status.value.rssi) || 0 : 0,
      ip: (status.value && status.value.ip) || '',
      err: '',
    };
    errorMessage.value = '';
    success.value = true;
    committing.value = false;
    connected.value = false;
    clearCommitWait(status.value);
    diag('GATT closed after Wi-Fi commit; treating the join as successful');
    return;
  }
  if (success.value) {
    connected.value = false;
    diag('GATT disconnected after Wi-Fi join');
    return;
  }
  connected.value = false;
  errorMessage.value = 'Lost the Bluetooth connection.';
  diag('GATT disconnected unexpectedly');
}

function onStatusChanged(event) {
  const raw = decodeUtf8(event.target.value);
  const parsed = parseStatusJson(raw);
  if (!parsed) {
    diag(`STATUS notify (unparsed): ${raw.slice(0, 120)}`);
    return;
  }
  status.value = parsed;
  diag(`STATUS ${JSON.stringify(parsed)}`);
  if (parsed.state === 'got_ip') {
    success.value = true;
    committing.value = false;
    clearCommitWait(parsed);
  } else if (parsed.state === 'bad_pin' || parsed.state === 'fail') {
    committing.value = false;
    success.value = false;
    clearCommitWait(parsed);
  }
}

function onScanChanged(event) {
  const raw = decodeUtf8(event.target.value);
  const parsed = parseScanJson(raw);
  scanResults.value = parsed;
  if (!parsed.length) {
    diag(`SCAN notify empty or unparsed: ${raw.slice(0, 120)}`);
    return;
  }
  diag(
    `SCAN ${parsed.length} AP(s): ${parsed.map((ap) => `${ap.s}(${ap.r})`).join(', ')}`,
  );
}

function selectSsid(ssid) {
  selectedSsid.value = ssid;
  manualSsid.value = ssid;
  diag(`selected SSID "${ssid}"`);
}

const CONTROL_NAMES = {
  [CONTROL.COMMIT]: 'COMMIT',
  [CONTROL.FORGET]: 'FORGET',
  [CONTROL.SCAN]: 'SCAN',
  [CONTROL.END]: 'END',
};

async function writeControl(byte) {
  if (!charControl) {
    diag('CONTROL write skipped (no characteristic)');
    return;
  }
  diag(`CONTROL 0x0${byte} ${CONTROL_NAMES[byte] || ''}`);
  await charControl.writeValue(Uint8Array.of(byte));
}

async function abort({ notifyDevice = true } = {}) {
  closed = true;
  commitSent = false;
  clearCommitWait(null);
  busy.value = false;
  committing.value = false;
  diag(`abort (notifyDevice=${notifyDevice})`);
  if (charStatus) {
    try {
      charStatus.removeEventListener(
        'characteristicvaluechanged',
        onStatusChanged,
      );
      await charStatus.stopNotifications();
    } catch (err) {
      diag(`stop STATUS notify: ${errDetail(err)}`);
    }
  }
  if (charScan) {
    try {
      charScan.removeEventListener('characteristicvaluechanged', onScanChanged);
      await charScan.stopNotifications();
    } catch (err) {
      diag(`stop SCAN notify: ${errDetail(err)}`);
    }
  }
  if (notifyDevice && charControl && gattServer && gattServer.connected) {
    try {
      await writeControl(CONTROL.END);
    } catch (err) {
      diag(`CONTROL END failed: ${errDetail(err)}`);
    }
  }
  if (bleDevice) {
    bleDevice.removeEventListener('gattserverdisconnected', onDisconnected);
    try {
      if (bleDevice.gatt && bleDevice.gatt.connected) {
        bleDevice.gatt.disconnect();
        diag('GATT disconnect()');
      }
    } catch (err) {
      diag(`GATT disconnect failed: ${errDetail(err)}`);
    }
  }
  bleDevice = null;
  gattServer = null;
  charSsid = null;
  charPassword = null;
  charControl = null;
  charStatus = null;
  charScan = null;
  connected.value = false;
}

function withTimeout(promise, ms, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

async function findFluidBox() {
  if (!bleOk) {
    diag('Find FluidBox ignored: Web Bluetooth not available');
    return;
  }
  closed = false;
  busy.value = true;
  errorMessage.value = '';
  success.value = false;
  status.value = null;
  scanResults.value = [];
  diag('requestDevice() — chooser should open');
  try {
    const picked = await navigator.bluetooth.requestDevice(
      fluidBoxRequestDeviceOptions(),
    );
    diag(`chooser picked name="${picked.name || ''}" id=${picked.id || '?'}`);
    if (closed) {
      diag('setup closed during chooser; dropping device');
      try {
        picked.gatt.disconnect();
      } catch {
        /* ignore */
      }
      return;
    }
    bleDevice = picked;
    deviceName.value = picked.name || 'FluidBox';
    bleDevice.addEventListener('gattserverdisconnected', onDisconnected);
    diag('GATT connect…');
    gattServer = await withTimeout(
      bleDevice.gatt.connect(),
      CONNECT_TIMEOUT_MS,
      'Timed out connecting to the FluidBox.',
    );
    diag(`GATT connected=${Boolean(gattServer && gattServer.connected)}`);
    const service = await gattServer.getPrimaryService(SERVICE_UUID);
    diag('got primary service');
    charSsid = await service.getCharacteristic(CHAR_SSID);
    charPassword = await service.getCharacteristic(CHAR_PASSWORD);
    charControl = await service.getCharacteristic(CHAR_CONTROL);
    charStatus = await service.getCharacteristic(CHAR_STATUS);
    charScan = await service.getCharacteristic(CHAR_SCAN);
    diag('got SSID/PASSWORD/CONTROL/STATUS/SCAN characteristics');
    charStatus.addEventListener('characteristicvaluechanged', onStatusChanged);
    charScan.addEventListener('characteristicvaluechanged', onScanChanged);
    await charStatus.startNotifications();
    await charScan.startNotifications();
    diag('STATUS + SCAN notifications started');
    try {
      const initial = parseStatusJson(decodeUtf8(await charStatus.readValue()));
      if (initial) {
        status.value = initial;
        diag(`initial STATUS ${JSON.stringify(initial)}`);
      } else {
        diag('initial STATUS read was empty/unparsed');
      }
    } catch (err) {
      diag(`initial STATUS read skipped: ${errDetail(err)}`);
    }
    await writeControl(CONTROL.SCAN);
    connected.value = true;
    diag('ready — pick an SSID');
  } catch (err) {
    diag(`findFluidBox failed: ${errDetail(err)}`);
    errorMessage.value = chooserErrorMessage(err);
    await abort({ notifyDevice: false });
  } finally {
    busy.value = false;
  }
}

async function saveAndConnect() {
  if (!canCommit.value) return;
  committing.value = true;
  commitSent = false;
  success.value = false;
  errorMessage.value = '';
  const ssid = manualSsid.value.trim().slice(0, 32);
  diag(
    `Save & connect SSID="${ssid}" pinLen=${digitsOnly(pin.value).length} passLen=${wifiPassword.value.length}`,
  );
  try {
    await charSsid.writeValue(encodeUtf8(ssid));
    diag('wrote SSID');
    await charPassword.writeValue(
      encodePasswordPayload(pin.value, wifiPassword.value),
    );
    diag('wrote PASSWORD (PIN\\nsecret, not logged)');
    const outcome = new Promise((resolve, reject) => {
      commitWait = {
        resolve,
        timer: setTimeout(() => {
          reject(
            new Error('Timed out waiting for the FluidBox to join Wi-Fi.'),
          );
        }, COMMIT_TIMEOUT_MS),
      };
    });
    await writeControl(CONTROL.COMMIT);
    commitSent = true;
    diag('waiting for STATUS got_ip / bad_pin / fail');
    await outcome;
    diag('commit finished');
  } catch (err) {
    if (success.value) {
      committing.value = false;
      return;
    }
    diag(`Save & connect failed: ${errDetail(err)}`);
    errorMessage.value = chooserErrorMessage(err);
    committing.value = false;
    clearCommitWait(null);
  }
}

async function forgetNetwork() {
  if (!charControl) return;
  busy.value = true;
  errorMessage.value = '';
  diag('Forget network');
  try {
    await writeControl(CONTROL.FORGET);
    status.value = { state: 'idle', ssid: '', rssi: 0, ip: '', err: '' };
    success.value = false;
  } catch (err) {
    diag(`Forget failed: ${errDetail(err)}`);
    errorMessage.value = chooserErrorMessage(err);
  } finally {
    busy.value = false;
  }
}

async function finish() {
  wifiPassword.value = '';
  showWifiPassword.value = false;
  await abort({ notifyDevice: true });
  emit('close');
}

async function cancel() {
  wifiPassword.value = '';
  showWifiPassword.value = false;
  await abort({ notifyDevice: true });
  emit('close');
}

async function logEnvironment() {
  diag(`secureContext=${window.isSecureContext}`);
  diag(`bleOk=${bleOk}`);
  diag(`ua=${navigator.userAgent}`);
  if (!navigator.bluetooth) {
    diag('navigator.bluetooth is undefined');
    return;
  }
  diag('navigator.bluetooth present');
  if (typeof navigator.bluetooth.getAvailability === 'function') {
    try {
      diag(`getAvailability=${await navigator.bluetooth.getAvailability()}`);
    } catch (err) {
      diag(`getAvailability failed: ${errDetail(err)}`);
    }
  }
}

onMounted(() => {
  logEnvironment();
});

onUnmounted(() => {
  abort({ notifyDevice: true });
});

defineExpose({ abort });
</script>

<style scoped>
.fb-setup {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
  flex: 1;
  min-height: 0;
  height: 100%;
}

.fb-main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  -webkit-overflow-scrolling: touch;
}

.fb-lead,
.fb-hint,
.fb-device,
.fb-android-only {
  margin: 0;
  font-size: 0.95em;
  line-height: 1.4;
  color: #444;
}

.fb-android-only {
  background: #f6f0f6;
  border-radius: 6px;
  padding: 12px;
  color: #4a3c4c;
}

.fb-device {
  font-weight: 600;
  color: #675769;
}

.fb-status {
  margin: 0;
  font-size: 0.9em;
  color: #555;
}

.fb-status.error {
  color: #c62828;
}

.fb-button {
  width: 100%;
  height: 44px;
  min-height: 44px;
  flex-shrink: 0;
  box-sizing: border-box;
  border: none;
  border-radius: 4px;
  background: #947797;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fb-button:disabled {
  opacity: 0.55;
  cursor: default;
}

.fb-button-secondary {
  background: #7a6b7c;
}

.fb-button-cancel {
  background: #999;
}

.fb-scan-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fb-ap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 15px;
}

.fb-ap.selected {
  border-color: #947797;
  background: #f6eef6;
}

.fb-ap-name {
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fb-ap-rssi {
  color: #777;
  font-size: 0.85em;
  margin-left: 8px;
  flex-shrink: 0;
}

.fb-field {
  text-align: left;
}

.fb-field label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.fb-field input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #ddd;
  border-radius: 5px;
  padding: 10px;
  outline: none;
  font-size: 16px;
}

.fb-password-wrap {
  position: relative;
}

.fb-password-wrap input {
  padding-right: 44px;
}

.fb-password-toggle {
  position: absolute;
  right: 0;
  top: 0;
  width: 44px;
  height: 100%;
  border: none;
  background: transparent;
  color: #675769;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  padding: 0;
}

.fb-diag {
  display: flex;
  flex-direction: column;
  flex: 0 0 34%;
  min-height: 8rem;
  max-height: 38%;
  background: #111;
  color: #18fb18;
  border-radius: 5px;
  font-size: 0.7em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  box-shadow: inset 0 0 0 1px #222;
  overflow: hidden;
}

.fb-diag-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px 0;
  color: #7dff7d;
  flex-shrink: 0;
}

.fb-diag-clear {
  border: 1px solid #444;
  background: #222;
  color: #18fb18;
  border-radius: 4px;
  font-size: 0.95em;
  padding: 2px 8px;
  cursor: pointer;
}

.fb-diag-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 6px 8px 10px;
  line-height: 1.35;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
